import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";

const STORAGE_PREFIX = "collapsible-section:";

function readStored(key: string | undefined, fallback: boolean): boolean {
  if (!key) return fallback;
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + key);
    if (raw === "1") return true;
    if (raw === "0") return false;
  } catch {
    // ignore — localStorage may be unavailable (private mode, SSR)
  }
  return fallback;
}

function writeStored(key: string, value: boolean) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_PREFIX + key, value ? "1" : "0");
  } catch {
    // ignore
  }
}

/**
 * Reusable collapsible section card. Matches the existing assessment
 * section card styling (rounded border, bg-card, shadow, padding) so
 * the visual design is unchanged when expanded. The header is a real
 * <button> with aria-expanded / aria-controls for keyboard accessibility
 * (Enter/Space toggle). Sections start expanded by default.
 *
 * Supports both controlled (``open`` + ``onOpenChange``) and
 * uncontrolled (``defaultOpen`` / ``storageKey``) usage.
 *
 * ``storageKey`` — when set, the open/closed state is persisted in
 * localStorage under that key so it survives reloads. Scope the key
 * per-instance (e.g. include an agent / team / assessment id) so
 * toggling one card doesn't affect another.
 *
 * ``forceOpen`` — when true, the section is always rendered open
 * regardless of stored state. Use this to keep a section open when an
 * active filter is targeting it so users don't lose track of what's
 * filtered. The user's stored preference is preserved underneath, so
 * when the filter clears the section returns to whatever they last
 * chose.
 *
 * ``headerClassName`` — extra classes appended to the header button
 * (e.g. ``hover-elevate`` for the house interactive-feedback overlay).
 * Optional and additive, so existing consumers are unaffected.
 */
export function CollapsibleSection({
  title,
  children,
  rightAdornment,
  defaultOpen = true,
  open: controlledOpen,
  onOpenChange,
  storageKey,
  forceOpen = false,
  bodyClassName,
  headerClassName,
}: {
  title: ReactNode;
  children: ReactNode;
  rightAdornment?: ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (next: boolean) => void;
  storageKey?: string;
  forceOpen?: boolean;
  bodyClassName?: string;
  headerClassName?: string;
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(() =>
    readStored(storageKey, defaultOpen),
  );
  // Track the key that ``uncontrolledOpen`` currently belongs to so we
  // never accidentally write one instance's state into another's
  // storage slot when the component is reused across different
  // entities (e.g. AgentProfileView navigating from agent A to B).
  const hydratedKeyRef = useRef<string | undefined>(storageKey);
  const isControlled = controlledOpen !== undefined;

  // Rehydrate from localStorage whenever the storageKey changes in
  // uncontrolled mode. Without this, the same mounted instance would
  // keep showing the previous entity's collapse state — and the
  // persistence effect below would happily overwrite the new key
  // with that stale value.
  useEffect(() => {
    if (isControlled) {
      hydratedKeyRef.current = storageKey;
      return;
    }
    if (hydratedKeyRef.current === storageKey) return;
    hydratedKeyRef.current = storageKey;
    setUncontrolledOpen(readStored(storageKey, defaultOpen));
  }, [storageKey, isControlled, defaultOpen]);

  const storedOrControlled = isControlled ? controlledOpen : uncontrolledOpen;
  const open = forceOpen ? true : storedOrControlled;
  const contentId = useId();

  // Persist stored preference whenever the user toggles in uncontrolled
  // mode. We persist the *user's* choice, not the forced-open state, so
  // the section restores correctly once the filter clears. Guarded on
  // ``hydratedKeyRef`` so a key change doesn't write the previous
  // entity's state into the new key before the rehydrate effect runs.
  useEffect(() => {
    if (!storageKey || isControlled) return;
    if (hydratedKeyRef.current !== storageKey) return;
    writeStored(storageKey, uncontrolledOpen);
  }, [storageKey, isControlled, uncontrolledOpen]);

  const toggle = () => {
    const next = !open;
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  return (
    <section className="rounded-lg border border-border bg-card shadow-md animate-card-enter">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={contentId}
        className={`w-full flex items-center justify-between gap-2 p-4 text-left${
          headerClassName ? ` ${headerClassName}` : ""
        }`}
      >
        <div className="flex-1 min-w-0">
          {typeof title === "string" ? (
            <h3 className="font-semibold text-foreground">{title}</h3>
          ) : (
            title
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {rightAdornment}
          <ChevronDown
            aria-hidden="true"
            className={`h-4 w-4 text-muted-foreground transition-transform duration-[var(--duration-base)] ease-[var(--easing-standard)] ${
              open ? "rotate-0" : "-rotate-90"
            }`}
          />
        </div>
      </button>
      {open && (
        <div
          id={contentId}
          className={bodyClassName ?? "px-4 pb-4 space-y-3"}
        >
          {children}
        </div>
      )}
    </section>
  );
}
